import { useMemo } from 'react';
import { ConfigurationGroupDeviceType } from '@casops/store/apis/commonModels';
import { ValueGetterFunc, ValueGetterParams } from 'ag-grid-community';
import i18n from '@casops/i18n';

export enum CompatibleSoftware {
  Gen2 = 'Gen2',
  Gen3 = 'Gen3',
  Any = 'Any',
}

export const compatibleSoftwareMap: Record<ConfigurationGroupDeviceType, CompatibleSoftware[]> = {
  X1: [CompatibleSoftware.Gen2],
  X9: [CompatibleSoftware.Gen3],
  IVU: [CompatibleSoftware.Gen2],
  IVUPlus: [CompatibleSoftware.Gen3],
  Generic: [CompatibleSoftware.Any],
};

const getCompatibleSoftwareLabels = (
  deviceType: ConfigurationGroupDeviceType,
  isVerbose = false,
) => {
  return compatibleSoftwareMap[deviceType]
    .map(software => {
      // Enable more detailed 'Any' labels for contexts where both software and device labels are shown together.
      if (software === CompatibleSoftware.Any && isVerbose) {
        return i18n.t('AnySoftware');
      } else {
        return i18n.t(`CompatibleSoftware.${software}`);
      }
    })
    .sort((a, b) => a.localeCompare(b));
};

export const useCompatibleSoftwareLabels = (
  deviceType?: ConfigurationGroupDeviceType,
  isVerbose?: boolean,
): string[] => {
  const softwareLabels = useMemo<string[]>(() => {
    return typeof deviceType !== 'undefined'
      ? getCompatibleSoftwareLabels(deviceType, isVerbose)
      : [];
  }, [deviceType, isVerbose]);

  return softwareLabels;
};

export const compatibleSoftwareValueGetter = <TData extends { hw: ConfigurationGroupDeviceType }>({
  data,
}: ValueGetterParams<TData, string[]>): ReturnType<ValueGetterFunc<TData, string[]>> => {
  return typeof data?.hw === 'string' ? getCompatibleSoftwareLabels(data.hw) : [];
};
